import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, getAuthHeader } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import { LogOut, Users, School, ClipboardList, ChevronRight, Plus, Download, Search, Upload, Trash2, Package, AlertTriangle, ScrollText, RefreshCw } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [schools, setSchools] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", school_name: "" });
  const [search, setSearch] = useState("");
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [schoolTours, setSchoolTours] = useState([]);
  const [deleteSchool, setDeleteSchool] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [activeTab, setActiveTab] = useState("schools");
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  useEffect(() => { loadDashboard(); }, []);

  const loadAuditLogs = async () => {
    setAuditLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/audit-logs?limit=200`, { headers: getAuthHeader() });
      setAuditLogs(data.logs || []);
    } catch { toast.error("Failed to load audit logs"); }
    finally { setAuditLoading(false); }
  };

  const loadDashboard = async () => {
    try {
      const [d, s] = await Promise.all([
        axios.get(`${API}/api/admin/dashboard`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/admin/schools`, { headers: getAuthHeader() }),
      ]);
      setData(d.data);
      setSchools(s.data);
    } catch { toast.error("Failed to load dashboard"); }
  };

  const createSchool = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password || !form.school_name) { toast.error("All fields required"); return; }
    try {
      await axios.post(`${API}/api/auth/register-school`, form, { headers: getAuthHeader() });
      toast.success("School registered!");
      setShowCreate(false);
      setForm({ email: "", password: "", name: "", school_name: "" });
      loadDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to register school");
    }
  };

  const viewSchool = async (school) => {
    setSelectedSchool(school);
    const { data } = await axios.get(`${API}/api/admin/schools/${school.id}/tours`, { headers: getAuthHeader() });
    setSchoolTours(data);
  };

  const uploadSchoolLogo = async (e, schoolId) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post(`${API}/api/admin/schools/${schoolId}/logo`, fd, { headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" } });
      toast.success("School logo uploaded!");
      loadDashboard();
    } catch { toast.error("Logo upload failed"); }
    e.target.value = "";
  };

  const exportTour = async (tourId, tourName) => {
    try {
      const { data } = await axios.get(`${API}/api/admin/export/${tourId}`, { headers: getAuthHeader() });
      const csvRows = ["Full Name,Class,Section,Gender,Age,Blood Group,Student ID,Emergency Contact,Phone,Medical Conditions,Allergies"];
      (data.students || []).forEach(s => {
        csvRows.push([s.full_name, s.class_name, s.section, s.gender, s.age, s.blood_group, s.student_id, s.emergency_contact_name, s.emergency_contact_phone, s.medical_conditions, s.allergies].map(v => `"${(v || '').replace(/"/g, '""')}"`).join(","));
      });
      const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${tourName || "tour"}_students.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Exported!");
    } catch { toast.error("Export failed"); }
  };

  const handleLogout = async () => { await logout(); navigate("/login"); };

  const downloadAllSchoolData = async (school) => {
    if (!school) return;
    setIsDownloading(true);
    try {
      const res = await axios.get(`${API}/api/admin/schools/${school.id}/download-all`, {
        headers: getAuthHeader(),
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(school.name || "school").replace(/\s+/g, "_")}_all_data.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Download started");
    } catch {
      toast.error("Download failed");
    } finally {
      setIsDownloading(false);
    }
  };

  const confirmDeleteSchool = async () => {
    if (!deleteSchool) return;
    if (deleteConfirmText !== deleteSchool.name) {
      toast.error("School name doesn't match");
      return;
    }
    setIsDeleting(true);
    try {
      await axios.delete(`${API}/api/admin/schools/${deleteSchool.id}`, { headers: getAuthHeader() });
      toast.success(`Deleted "${deleteSchool.name}"`);
      setDeleteSchool(null);
      setDeleteConfirmText("");
      if (selectedSchool?.id === deleteSchool.id) {
        setSelectedSchool(null);
        setSchoolTours([]);
      }
      loadDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    } finally {
      setIsDeleting(false);
    }
  };

  const filteredSchools = schools.filter(s => !search || s.name.toLowerCase().includes(search.toLowerCase()));
  const statusColors = { draft: "bg-yellow-100 text-yellow-700", submitted: "bg-blue-100 text-blue-700", completed: "bg-green-100 text-green-700" };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      <header className="bg-primary text-primary-foreground px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="font-heading text-xl font-medium">OSAA Super Admin</h1>
            <p className="text-sm text-primary-foreground/70">All schools overview</p>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/portal" className="text-sm text-primary-foreground/80 hover:text-primary-foreground border border-primary-foreground/30 px-3 py-1.5 rounded-sm">Portal</Link>
            <button onClick={handleLogout} className="inline-flex items-center gap-1.5 text-sm text-primary-foreground/80 hover:text-primary-foreground">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        {data && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-card border border-border/60 p-5 rounded-sm">
              <School className="w-5 h-5 text-accent mb-2" />
              <div className="font-heading text-2xl font-semibold">{data.total_schools}</div>
              <div className="text-xs text-muted-foreground uppercase tracking-wider">Schools</div>
            </div>
            <div className="bg-card border border-border/60 p-5 rounded-sm">
              <ClipboardList className="w-5 h-5 text-accent mb-2" />
              <div className="font-heading text-2xl font-semibold">{data.total_tours}</div>
              <div className="text-xs text-muted-foreground uppercase tracking-wider">Tours</div>
            </div>
            <div className="bg-card border border-border/60 p-5 rounded-sm">
              <Users className="w-5 h-5 text-accent mb-2" />
              <div className="font-heading text-2xl font-semibold">{data.total_students}</div>
              <div className="text-xs text-muted-foreground uppercase tracking-wider">Students</div>
            </div>
            <div className="bg-card border border-border/60 p-5 rounded-sm">
              <Users className="w-5 h-5 text-accent mb-2" />
              <div className="font-heading text-2xl font-semibold">{data.total_staff}</div>
              <div className="text-xs text-muted-foreground uppercase tracking-wider">Staff</div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-border/60 mb-6">
          <button
            onClick={() => setActiveTab("schools")}
            data-testid="tab-schools"
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === "schools" ? "border-accent text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          >
            Schools & Tours
          </button>
          <button
            onClick={() => { setActiveTab("audit"); loadAuditLogs(); }}
            data-testid="tab-audit"
            className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === "audit" ? "border-accent text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          >
            <ScrollText className="w-3.5 h-3.5" /> Audit Log
          </button>
        </div>

        {activeTab === "schools" && (
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Schools List */}
          <div className="lg:col-span-1">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl font-medium">Schools</h2>
              <button onClick={() => setShowCreate(!showCreate)} data-testid="register-school-btn"
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-accent text-accent-foreground text-xs font-medium rounded-sm">
                <Plus className="w-3.5 h-3.5" /> Add
              </button>
            </div>

            {showCreate && (
              <div className="bg-card border border-border/60 p-4 rounded-sm mb-4" data-testid="register-school-form">
                <form onSubmit={createSchool} className="space-y-3">
                  <Input placeholder="School Name *" value={form.school_name} onChange={e => setForm(p => ({...p, school_name: e.target.value}))} className="text-sm" />
                  <Input placeholder="Admin Name *" value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} className="text-sm" />
                  <Input placeholder="Email *" type="email" value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} className="text-sm" />
                  <Input placeholder="Password *" type="password" value={form.password} onChange={e => setForm(p => ({...p, password: e.target.value}))} className="text-sm" />
                  <div className="flex gap-2">
                    <button type="submit" className="px-4 py-2 bg-primary text-primary-foreground text-xs rounded-sm">Register</button>
                    <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2 border border-border/60 text-xs rounded-sm">Cancel</button>
                  </div>
                </form>
              </div>
            )}

            <div className="relative mb-3">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder="Search schools..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 text-sm" />
            </div>

            <div className="space-y-2">
              {filteredSchools.map(s => (
                <button key={s.id} onClick={() => viewSchool(s)} data-testid={`school-${s.id}`}
                  className={`w-full flex items-center gap-3 p-3 rounded-sm border text-left transition-colors ${selectedSchool?.id === s.id ? 'border-primary bg-primary/5' : 'border-border/60 bg-card hover:bg-muted/30'}`}>
                  <School className="w-5 h-5 text-primary shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{s.name}</p>
                    <p className="text-xs text-muted-foreground">{s.tour_count || 0} tours &middot; {s.student_count || 0} students</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </button>
              ))}
              {filteredSchools.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">No schools registered yet.</p>}
            </div>
          </div>

          {/* School Detail / All Tours */}
          <div className="lg:col-span-2">
            {selectedSchool ? (
              <>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="font-heading text-xl font-medium">{selectedSchool.name}</h2>
                    <p className="text-xs text-muted-foreground">{schoolTours.length} tours</p>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap justify-end">
                    <label className="inline-flex items-center gap-1 px-3 py-1.5 border border-border/60 text-xs rounded-sm cursor-pointer hover:bg-muted">
                      <Upload className="w-3.5 h-3.5" /> Logo
                      <input type="file" accept="image/*" onChange={(e) => uploadSchoolLogo(e, selectedSchool.id)} className="hidden" />
                    </label>
                    <button
                      onClick={() => downloadAllSchoolData(selectedSchool)}
                      disabled={isDownloading}
                      data-testid="download-all-school-btn"
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary text-primary-foreground text-xs rounded-sm disabled:opacity-60"
                    >
                      <Package className="w-3.5 h-3.5" /> {isDownloading ? "Packaging..." : "Download All"}
                    </button>
                    <button
                      onClick={() => { setDeleteSchool(selectedSchool); setDeleteConfirmText(""); }}
                      data-testid="delete-school-btn"
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-destructive text-destructive-foreground text-xs rounded-sm hover:opacity-90"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                    <button onClick={() => setSelectedSchool(null)} className="text-sm text-muted-foreground hover:text-foreground">View All Tours</button>
                  </div>
                </div>
                <div className="space-y-3">
                  {schoolTours.map(t => (
                    <div key={t.id} className="flex items-center gap-4 bg-card border border-border/60 p-4 rounded-sm">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-sm">{t.name}</span>
                          <span className={`text-[10px] font-medium uppercase px-2 py-0.5 rounded-full ${statusColors[t.status] || ''}`}>{t.status}</span>
                        </div>
                        <p className="text-xs text-muted-foreground">{t.program_type} &middot; {t.student_count || 0} students &middot; {t.staff_count || 0} staff</p>
                      </div>
                      <button onClick={() => exportTour(t.id, t.name)} className="inline-flex items-center gap-1 text-xs text-primary hover:text-accent">
                        <Download className="w-3.5 h-3.5" /> Export
                      </button>
                      <Link to={`/portal/tours/${t.id}`} className="text-xs text-primary hover:text-accent">View</Link>
                    </div>
                  ))}
                  {schoolTours.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">No tours for this school.</p>}
                </div>
              </>
            ) : (
              <>
                <h2 className="font-heading text-xl font-medium mb-4">All Tours</h2>
                <div className="space-y-3">
                  {data?.tours?.map(t => (
                    <div key={t.id} className="flex items-center gap-4 bg-card border border-border/60 p-4 rounded-sm">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-sm">{t.name}</span>
                          <span className={`text-[10px] font-medium uppercase px-2 py-0.5 rounded-full ${statusColors[t.status] || ''}`}>{t.status}</span>
                        </div>
                        <p className="text-xs text-muted-foreground">{t.school_name || "-"} &middot; {t.program_type} &middot; {t.student_count || 0} students</p>
                      </div>
                      <button onClick={() => exportTour(t.id, t.name)} className="inline-flex items-center gap-1 text-xs text-primary hover:text-accent">
                        <Download className="w-3.5 h-3.5" /> Export
                      </button>
                      <Link to={`/portal/tours/${t.id}`} className="text-xs text-primary hover:text-accent">View</Link>
                    </div>
                  ))}
                  {(!data?.tours || data.tours.length === 0) && <p className="text-sm text-muted-foreground text-center py-8">No tours yet.</p>}
                </div>
              </>
            )}
          </div>
        </div>
        )}

        {activeTab === "audit" && (
          <div data-testid="audit-log-panel">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-heading text-xl font-medium">Audit Log</h2>
                <p className="text-xs text-muted-foreground">Destructive and administrative actions across the portal</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    const t = localStorage.getItem("token") || "";
                    window.location.href = `${API}/api/admin/audit-logs/csv?token=${encodeURIComponent(t)}`;
                  }}
                  data-testid="audit-export-csv-btn"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-border/60 text-xs rounded-sm hover:bg-muted"
                >
                  <Download className="w-3.5 h-3.5" /> Export CSV
                </button>
                <button
                  onClick={loadAuditLogs}
                  disabled={auditLoading}
                  data-testid="audit-refresh-btn"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-border/60 text-xs rounded-sm hover:bg-muted disabled:opacity-60"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${auditLoading ? "animate-spin" : ""}`} /> Refresh
                </button>
              </div>
            </div>
            <div className="bg-card border border-border/60 rounded-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Time</th>
                    <th className="text-left px-4 py-2 font-medium">User</th>
                    <th className="text-left px-4 py-2 font-medium">Action</th>
                    <th className="text-left px-4 py-2 font-medium">Entity</th>
                    <th className="text-left px-4 py-2 font-medium">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.map((log) => {
                    const isDelete = log.action?.endsWith(".delete");
                    return (
                      <tr key={log.id} className="border-t border-border/60" data-testid={`audit-row-${log.id}`}>
                        <td className="px-4 py-2 text-xs text-muted-foreground whitespace-nowrap">
                          {new Date(log.timestamp).toLocaleString()}
                        </td>
                        <td className="px-4 py-2 text-xs">{log.user_email}</td>
                        <td className="px-4 py-2">
                          <span className={`text-[10px] font-medium uppercase px-2 py-0.5 rounded-full ${isDelete ? "bg-destructive/10 text-destructive" : "bg-primary/10 text-primary"}`}>
                            {log.action}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-xs">
                          <span className="font-medium">{log.entity_name || log.entity_id || "-"}</span>
                          {log.entity_type && <span className="text-muted-foreground"> ({log.entity_type})</span>}
                        </td>
                        <td className="px-4 py-2 text-xs text-muted-foreground">
                          {log.details && Object.keys(log.details).length > 0
                            ? Object.entries(log.details).map(([k, v]) => `${k}: ${v}`).join(", ")
                            : "-"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {!auditLoading && auditLogs.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-10">No audit entries yet.</p>
              )}
              {auditLoading && (
                <p className="text-sm text-muted-foreground text-center py-10">Loading...</p>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Delete School Confirmation Modal */}
      {deleteSchool && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4" data-testid="delete-school-modal">
          <div className="bg-card border border-border rounded-sm max-w-md w-full p-6 shadow-xl">
            <div className="flex items-start gap-3 mb-4">
              <div className="shrink-0 w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-destructive" />
              </div>
              <div>
                <h3 className="font-heading text-lg font-medium">Delete school permanently?</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  This will permanently delete <strong>{deleteSchool.name}</strong>, its users, tours, students, staff, certificates and gallery. This action cannot be undone.
                </p>
              </div>
            </div>
            <div className="bg-destructive/5 border border-destructive/30 rounded-sm p-3 mb-4">
              <p className="text-xs text-muted-foreground mb-2">Type the school name to confirm:</p>
              <p className="font-mono text-xs font-medium mb-2 select-all">{deleteSchool.name}</p>
              <Input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Type school name"
                data-testid="delete-school-confirm-input"
                className="text-sm"
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setDeleteSchool(null); setDeleteConfirmText(""); }}
                className="px-4 py-2 border border-border/60 text-xs rounded-sm"
                data-testid="delete-school-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteSchool}
                disabled={isDeleting || deleteConfirmText !== deleteSchool.name}
                className="px-4 py-2 bg-destructive text-destructive-foreground text-xs rounded-sm disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="delete-school-confirm-btn"
              >
                {isDeleting ? "Deleting..." : "Delete School"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
